import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { downloadSignedAgreement, acceptPrivateReview, approvePublication, submitReviewCorrection, checkoutMediaSelects, confirmMediaSelectPayment, getMediaAccessUrl, getMediaSelectDeliveryUrl, getPortal, submitCommercialInquiry, submitReferral } from '../lib/api';
import { demoMode, supabase } from '../lib/supabase';
import type { FeaturePortal, MediaItem, SelectTier } from '../types';
import { Brand } from '../components/Brand';
import { AgreementGate } from '../components/AgreementGate';
import { ReferralForm } from '../components/ReferralForm';
import { PrivateReviewVideo } from '../components/PrivateReviewVideo';
import { LegalDocumentText } from '../components/LegalDocumentText';

function formatPlatform(platform: string) {
  return platform === 'ROAM' ? 'ROAM GROUP' : platform;
}

function money(cents?: number | null) {
  if (cents == null) return 'Request';
  return cents === 0 ? '$0' : `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;
}

function formatOrderDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
}

function ProtectedMedia({ item, url, className = '', eager = false }: { item: MediaItem; url?: string; className?: string; eager?: boolean }) {
  if (!url) return <div className={`media-placeholder ${className}`}><span>Preparing protected preview…</span></div>;
  if (item.kind === 'video') return <PrivateReviewVideo className={className} src={url} title={item.title} eager={eager} />;
  if (item.kind === 'photo') return <img className={`protected-image ${className}`} src={url} alt={item.title} />;
  return <div className={`media-placeholder ${className}`}><strong>{item.title}</strong><span>Protected document</span></div>;
}

function tierLabel(tier: SelectTier) {
  if (tier === 'share') return 'ROAM Share';
  if (tier === 'clean') return 'Clean Select';
  return 'Prepared by ROAM';
}

function tierPrice(item: MediaItem, tier: SelectTier) {
  if (tier === 'share') return 0;
  if (tier === 'clean') return item.cleanPriceCents ?? 0;
  return item.preparedPriceCents ?? 0;
}

function MediaSelectCard({ item, url, selectedTier, onAdd, onRemove }: { item: MediaItem; url?: string; selectedTier?: SelectTier; onAdd: (mediaId: string, tier: SelectTier) => void; onRemove: (mediaId: string) => void }) {
  const defaultTier: SelectTier = item.freeWatermarked ? 'share' : item.cleanPriceCents != null ? 'clean' : 'prepared';
  const [tier, setTier] = useState<SelectTier>(selectedTier ?? defaultTier);

  useEffect(() => {
    if (selectedTier) setTier(selectedTier);
  }, [selectedTier]);

  const added = Boolean(selectedTier);
  const upToDate = selectedTier === tier;

  return (
    <article className="select-card">
      <div className="select-preview"><ProtectedMedia item={item} url={url} />{tier === 'share' && item.freeWatermarked && <div className="preview-watermark">ROAM GROUP</div>}</div>
      <div className="select-card__copy">
        <div className="eyebrow">{item.kind === 'video' ? 'VIDEO SELECT' : 'PHOTO SELECT'}</div>
        <h3>{item.title}</h3>
        {item.description && <p>{item.description}</p>}
        <div className="tier-picker">
          {item.freeWatermarked && <button type="button" className={tier === 'share' ? 'tier-button tier-button--active' : 'tier-button'} onClick={() => setTier('share')}><strong>ROAM Share</strong><span>Complimentary · watermarked</span></button>}
          {item.cleanPriceCents != null && <button type="button" className={tier === 'clean' ? 'tier-button tier-button--active' : 'tier-button'} onClick={() => setTier('clean')}><strong>Clean Select</strong><span>{money(item.cleanPriceCents)} · limited use</span></button>}
          {item.preparedPriceCents != null && <button type="button" className={tier === 'prepared' ? 'tier-button tier-button--active' : 'tier-button'} onClick={() => setTier('prepared')}><strong>Prepared by ROAM</strong><span>{money(item.preparedPriceCents)} · color graded</span></button>}
        </div>
        <button type="button" className="button button--dark button--wide" onClick={() => onAdd(item.id, tier)} disabled={upToDate}>{added ? upToDate ? 'Added to selections ✓' : 'Update selection' : 'Add to selections'}</button>
        {added && <button type="button" className="text-link text-link--button select-remove" onClick={() => onRemove(item.id)}>Remove from selections</button>}
      </div>
    </article>
  );
}

function MediaSelectCheckout({ portal, items, onClose, onRemove, onSubmitted }: { portal: FeaturePortal; items: { item: MediaItem; tier: SelectTier; priceCents: number }[]; onClose: () => void; onRemove: (mediaId: string) => void; onSubmitted: (message: string) => void }) {
  const [support, setSupport] = useState('0');
  const [note, setNote] = useState('');
  const [typedName, setTypedName] = useState(portal.participantName);
  const [accepted, setAccepted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const license = portal.legal.mediaSelectLicense;
  const subtotal = items.reduce((sum, entry) => sum + entry.priceCents, 0);
  const supportCents = Math.max(0, Math.round(Number(support || 0) * 100));
  const total = subtotal + supportCents;

  async function submit() {
    if (!accepted || typedName.trim().length < 2 || items.length === 0) return;
    setSending(true); setError('');
    try {
      const result = await checkoutMediaSelects(portal.slug, {
        items: items.map(({ item, tier }) => ({ mediaId: item.id, tier })),
        supportCents,
        note,
        typedName: typedName.trim(),
        licenseAccepted: true,
      });
      if (result.paymentRequired && result.checkoutUrl) {
        window.location.assign(result.checkoutUrl);
        return;
      }
      onSubmitted(`Media Select request ${result.orderId.slice(0, 8).toUpperCase()} received. One license acceptance was recorded for all ${items.length} selection${items.length === 1 ? '' : 's'} in this checkout.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to submit Media Select request.');
    } finally { setSending(false); }
  }

  return <div className="checkout-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <section className="media-checkout" role="dialog" aria-modal="true" aria-label="Review Media Selects">
      <header className="media-checkout__header"><div><div className="eyebrow">ROAM MEDIA SELECTS · CHECKOUT</div><h2>Review your selections.</h2><p>One limited-use license covers everything listed in this request.</p></div><button className="checkout-close" type="button" aria-label="Close checkout" onClick={onClose}>×</button></header>
      <div className="checkout-summary">
        <div className="eyebrow">ORDER SUMMARY</div>
        {items.map(({ item, tier, priceCents }) => <div className="checkout-line" key={item.id}><div><strong>{item.title}</strong><span>{tierLabel(tier)}</span></div><div><strong>{money(priceCents)}</strong><button type="button" className="text-link text-link--button" onClick={() => onRemove(item.id)}>Remove</button></div></div>)}
        <div className="checkout-support"><label>Optional support ROAM ($)<input type="number" min="0" step="5" value={support} onChange={(e) => setSupport(e.target.value)} /></label><small>Support is optional and does not change the license, editorial coverage or publication.</small></div>
        <label className="checkout-note">Optional note<textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything we should know about this request?" /></label>
        <div className="checkout-totals"><div><span>Media</span><strong>{money(subtotal)}</strong></div><div><span>Optional support</span><strong>{money(supportCents)}</strong></div><div className="checkout-total"><span>Total request</span><strong>{money(total)}</strong></div></div>
      </div>
      <div className="checkout-license">
        <div className="eyebrow">ONE LIMITED-USE LICENSE</div>
        <h3>{license.name}</h3><small>Version {license.version}</small>
        <div className="checkout-license__scope"><strong>This acceptance applies to:</strong>{items.map(({ item, tier, priceCents }) => <span key={item.id}>{item.title} · {tierLabel(tier)} · {money(priceCents)}</span>)}{supportCents > 0 && <span>Optional ROAM support · {money(supportCents)}</span>}</div>
        <div className="select-license-copy"><LegalDocumentText body={license.body} /></div>
        <label>Typed legal name<input value={typedName} onChange={(e) => setTypedName(e.target.value)} /></label>
        <label className="inline-check"><input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} /><span>I agree to the ROAM Media Selects Limited-Use License for all media and package tiers listed in this checkout.</span></label>
        {error && <p className="error">{error}</p>}
      </div>
      <footer className="media-checkout__footer"><div><span>Total</span><strong>{money(total)}</strong></div><button type="button" className="button button--dark" onClick={submit} disabled={sending || !accepted || typedName.trim().length < 2 || items.length === 0}>{sending ? (total > 0 ? 'Opening secure checkout…' : 'Submitting request…') : (total > 0 ? 'Agree & continue to payment' : 'Agree & submit request')}</button></footer>
    </section>
  </div>;
}

function MediaSelectOrders({ portal, onError }: { portal: FeaturePortal; onError: (message: string) => void }) {
  const orders = portal.mediaSelectOrders ?? [];
  const totalSelections = orders.reduce((sum, order) => sum + order.items.length, 0);
  const attentionOrders = useMemo(() => orders.filter((order) => {
    const paid = order.paymentStatus === 'paid';
    const noPayment = order.paymentStatus === 'not_required';
    const paymentPending = !paid && !noPayment && order.totalCents > 0;
    const preparing = !paymentPending && order.items.some((item) => !item.downloadReady);
    return paymentPending || preparing;
  }), [orders]);
  const preferredOpenOrderId = useMemo(() => {
    const paymentPending = attentionOrders.find((order) => order.paymentStatus !== 'paid' && order.paymentStatus !== 'not_required' && order.totalCents > 0);
    return paymentPending?.id ?? attentionOrders[0]?.id ?? null;
  }, [attentionOrders, orders]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);

  // Keep a manually selected accordion stable while the drawer is open.
  // On every new opening, choose the most relevant unfinished order (or none).
  useEffect(() => {
    if (!libraryOpen) return;
    setOpenOrderId((current) => current && !orders.some((order) => order.id === current) ? preferredOpenOrderId : current);
  }, [libraryOpen, orders, preferredOpenOrderId]);

  function openLibrary() {
    setOpenOrderId(preferredOpenOrderId);
    setLibraryOpen(true);
  }

  if (!orders.length) return null;

  return <>
    <div className="participant-media-hub">
      <div>
        <div className="eyebrow">YOUR MEDIA</div>
        <strong>{totalSelections} Media Select{totalSelections === 1 ? '' : 's'} in your library</strong>
        <span>Your downloads, licenses and Media Select history live here.</span>
      </div>
      <div className="participant-media-hub__actions">
        {attentionOrders.length > 0 && <span className="participant-media-hub__attention">{attentionOrders.length} in progress</span>}
        <button type="button" className="button button--dark button--small" onClick={openLibrary}>View your media</button>
      </div>
    </div>

    {libraryOpen && <div className="media-library-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setLibraryOpen(false); }}>
      <aside className="media-library" role="dialog" aria-modal="true" aria-label="Your Media">
        <header className="media-library__header">
          <div>
            <div className="eyebrow">ROAM MEDIA SELECTS</div>
            <h2>Your Media.</h2>
            <p>{totalSelections} selection{totalSelections === 1 ? '' : 's'} across {orders.length} order{orders.length === 1 ? '' : 's'}.</p>
          </div>
          <button type="button" className="media-library__close" aria-label="Close your media" onClick={() => setLibraryOpen(false)}>×</button>
        </header>

        <div className="media-library__intro">
          <strong>Order history</strong>
          <span>Open an order to view payment details, notes, license-linked selections and available downloads.</span>
        </div>

        <div className="participant-orders participant-orders--drawer">
          <div className="participant-orders__list">
            {orders.map((order) => {
              const paid = order.paymentStatus === 'paid';
              const noPayment = order.paymentStatus === 'not_required';
              const paymentPending = !paid && !noPayment && order.totalCents > 0;
              const allReady = order.items.length > 0 && order.items.every((item) => item.downloadReady);
              const isOpen = openOrderId === order.id;
              const compactStatus = paymentPending ? 'PAYMENT PENDING' : allReady ? 'READY' : paid ? 'PAID · PREPARING' : 'PREPARING';
              const dateLabel = formatOrderDate(order.createdAt);

              return <article className={`participant-order${isOpen ? ' participant-order--open' : ''}`} key={order.id}>
                <button
                  type="button"
                  className="participant-order__summary"
                  aria-expanded={isOpen}
                  aria-controls={`participant-order-${order.id}`}
                  onClick={() => setOpenOrderId((current) => current === order.id ? null : order.id)}
                >
                  <div className="participant-order__summary-main">
                    <strong>Request {order.id.slice(0, 8).toUpperCase()}</strong>
                    <span>{dateLabel}{dateLabel ? ' · ' : ''}{order.items.length} Media Select{order.items.length === 1 ? '' : 's'}{order.note ? ' · Note included' : ''}</span>
                  </div>
                  <div className="participant-order__summary-side">
                    <span className={`payment-badge participant-order__compact-badge ${paymentPending ? 'payment-badge--pending_payment' : allReady ? 'payment-badge--paid' : `payment-badge--${order.paymentStatus}`}`}>{compactStatus}</span>
                  </div>
                  <span className="participant-order__chevron" aria-hidden="true">⌄</span>
                </button>

                {isOpen && <div className="participant-order__body" id={`participant-order-${order.id}`}>
                  {paid && <div className="order-thanks"><strong>Thank you for supporting ROAM.</strong><p>Your purchase helps us continue documenting meaningful local stories while keeping our editorial features free for the people we feature.</p>{order.supportCents > 0 && <p>And thank you for the additional <strong>{money(order.supportCents)}</strong> in support. We genuinely appreciate it.</p>}</div>}
                  {noPayment && <div className="order-thanks"><strong>Your complimentary selections are recorded.</strong><p>No payment was required. Thank you again for being part of the story and allowing ROAM to document it.</p></div>}
                  {paymentPending && <div className="order-lock-note"><strong>Payment pending.</strong><span>Paid Media Selects remain locked until Stripe confirms successful payment.</span></div>}
                  {order.note && <div className="participant-order__note"><div className="eyebrow">YOUR NOTE</div><p>{order.note}</p></div>}
                  <div className="participant-order__financials">
                    <div><span>Media</span><strong>{money(order.subtotalCents)}</strong></div>
                    {order.supportCents > 0 && <div><span>Optional support</span><strong>{money(order.supportCents)}</strong></div>}
                    <div className="participant-order__financial-total"><span>{paymentPending ? 'Total request' : noPayment ? 'Total' : 'Total paid'}</span><strong>{money(order.totalCents)}</strong></div>
                  </div>
                  <div className="participant-order__items">{order.items.map((item) => <div className="participant-order__item" key={item.requestId}><div><strong>{item.mediaTitle}</strong><span>{tierLabel(item.tier)} · {money(item.basePriceCents)}</span></div><div>{item.downloadReady ? <button className="button button--dark button--small" onClick={async () => { try { const url = await getMediaSelectDeliveryUrl(portal.slug, order.id, item.requestId); window.location.assign(url); } catch (e) { onError(e instanceof Error ? e.message : 'Unable to open this Media Select.'); } }}>Download</button> : <span className="delivery-status">{item.delivery?.status === 'ready' && item.basePriceCents > 0 && !paid ? 'Locked until payment' : item.delivery?.status === 'ready' ? 'Ready' : 'Preparing file'}</span>}</div></div>)}</div>
                </div>}
              </article>;
            })}
          </div>
        </div>
      </aside>
    </div>}
  </>;
}

function PublicationReviewCard({ portal, onReviewed }: { portal: FeaturePortal; onReviewed: (reviewedAt: string, typedName: string) => void }) {
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correction, setCorrection] = useState('');
  const [working, setWorking] = useState(false);
  const [correctionSent, setCorrectionSent] = useState(false);
  const [error, setError] = useState('');
  if (!portal.publicationApproval.required) return null;
  if (portal.publicationApproval.approvedAt) return <div className="approval-card"><div><div className="eyebrow">PUBLICATION REVIEW</div><h3>Thank you for reviewing.</h3><p>Your review confirmation was recorded. You can continue to enjoy your private feature and browse ROAM Media Selects.</p></div><span className="pill">Reviewed</span></div>;
  async function reviewed() {
    setWorking(true); setError('');
    try {
      const result = await approvePublication(portal.slug);
      onReviewed(result.approvedAt, result.typedName || '');
    } catch (e) { setError(e instanceof Error ? e.message : 'Unable to record your review.'); }
    finally { setWorking(false); }
  }
  async function reportCorrection() {
    if (correction.trim().length < 10) return;
    setWorking(true); setError('');
    try {
      await submitReviewCorrection(portal.slug, correction.trim());
      setCorrectionSent(true); setCorrectionOpen(false);
    } catch (e) { setError(e instanceof Error ? e.message : 'Unable to submit your correction.'); }
    finally { setWorking(false); }
  }
  return <div className="approval-card approval-card--legal"><div><div className="eyebrow">WHERE THE STORY TRAVELS · PUBLICATION REVIEW</div><h3>Ready for us to share the story?</h3><p>We'd love to hear whether the story looks accurate. Your Participant Release already authorizes ROAM to publish; this review is a courtesy and is not an approval requirement. If you don't respond within 14 days after we send your preview, ROAM may publish under the Release.</p>
    {correctionOpen && <div className="publication-legal"><label>What factual detail should we check?<textarea rows={4} value={correction} onChange={(e) => setCorrection(e.target.value)} placeholder="Describe the fact that needs correction, and what it should say instead." /></label><button className="button button--light" type="button" disabled={working || correction.trim().length < 10} onClick={reportCorrection}>{working ? 'Sending…' : 'Send factual correction'}</button></div>}
    {correctionSent && <p className="notice">Your factual correction has been sent to ROAM. We will review it.</p>}
    {error && <p className="error">{error}</p>}
    </div><div className="review-actions"><button className="button button--dark" type="button" disabled={working} onClick={reviewed}>{working ? 'Recording review…' : 'Looks good — share it'}</button><button className="text-link text-link--button" type="button" onClick={() => setCorrectionOpen(!correctionOpen)}>{correctionOpen ? 'Close correction form' : 'I have a factual correction'}</button></div></div>;
}

function CommissionForm({ slug }: { slug: string }) {
  const [projectType, setProjectType] = useState('Brand film');
  const [details, setDetails] = useState('');
  const [timeline, setTimeline] = useState('');
  const [budgetRange, setBudgetRange] = useState('Let’s discuss');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault(); setSending(true);
    try { await submitCommercialInquiry(slug, { projectType, details, timeline, budgetRange }); setSent(true); }
    finally { setSending(false); }
  }

  if (sent) return <div className="commercial-success"><span>REQUEST RECEIVED</span><strong>We’ll follow up directly.</strong><p>This commercial request is separate from your editorial ROAM feature.</p></div>;
  return <form className="commercial-form" onSubmit={submit}><label>What are you looking to create?<select value={projectType} onChange={(e) => setProjectType(e.target.value)}><option>Brand film</option><option>Drone production</option><option>Photography</option><option>Short-form social content</option><option>Digital advertising campaign</option><option>Something else</option></select></label><label>Tell us about it<textarea required rows={4} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="What should the finished project accomplish?" /></label><div className="commercial-form__row"><label>Timing<input value={timeline} onChange={(e) => setTimeline(e.target.value)} placeholder="Flexible / target date" /></label><label>Budget range<select value={budgetRange} onChange={(e) => setBudgetRange(e.target.value)}><option>Let’s discuss</option><option>$1,000–$2,500</option><option>$2,500–$5,000</option><option>$5,000–$10,000</option><option>$10,000+</option></select></label></div><button className="button button--light" disabled={sending}>{sending ? 'Sending…' : 'Discuss a commercial project'}</button></form>;
}

export function Portal() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const [portal, setPortal] = useState<FeaturePortal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  const [requestNotice, setRequestNotice] = useState('');
  const [selectCart, setSelectCart] = useState<Record<string, SelectTier>>({});
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const selectTrackRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    async function load() {
      try {
        if (!demoMode && supabase) {
          const { data } = await supabase.auth.getSession();
          if (!data.session) { navigate(`/login?next=${encodeURIComponent(`/portal/${slug}`)}`, { replace: true }); return; }
        }
        const p = await getPortal(slug);
        if (demoMode) {
          const review = localStorage.getItem(`roam-demo-review:${slug}`);
          const release = localStorage.getItem(`roam-demo-release:${slug}`);
          const approval = localStorage.getItem(`roam-demo-approved:${slug}`);
          if (review) Object.assign(p.legal.privateReview, JSON.parse(review));
          if (release) Object.assign(p.legal.featureRelease, JSON.parse(release));
          if (approval) Object.assign(p.publicationApproval, JSON.parse(approval));
        }
        setPortal(p);
      } catch (e) { setError(e instanceof Error ? e.message : 'Unable to load portal.'); }
      finally { setLoading(false); }
    }
    load();
  }, [slug, navigate]);

  useEffect(() => {
    if (!portal?.legal.privateReview.acceptedAt || demoMode) return;
    Promise.all(portal.media.map(async (item) => { try { return [item.id, await getMediaAccessUrl(item.id, 'inline')] as const; } catch { return [item.id, ''] as const; } })).then((pairs) => setMediaUrls(Object.fromEntries(pairs)));
  }, [portal?.legal.privateReview.acceptedAt, portal?.media]);

  useEffect(() => {
    if (!portal?.id || demoMode) return;
    const paymentSlug = portal.slug;
    const params = new URLSearchParams(window.location.search);
    const stripeState = params.get('stripe');
    const orderId = params.get('order');
    const sessionId = params.get('session_id');
    if (!stripeState) return;

    let cancelled = false;
    async function finishReturn() {
      if (stripeState === 'cancelled') {
        if (!cancelled) setRequestNotice('Payment was not completed. No charge was made. You can choose your Media Selects again when you are ready.');
      } else if (stripeState === 'success' && orderId && sessionId) {
        try {
          const result = await confirmMediaSelectPayment(paymentSlug, orderId, sessionId);
          if (!cancelled) {
            const supportThanks = result.supportCents && result.supportCents > 0 ? ` And thank you for the additional ${money(result.supportCents)} in support — we genuinely appreciate it.` : '';
            setRequestNotice(`Payment confirmed · ${money(result.totalCents)}. Thank you for supporting ROAM. Purchases like this help us keep our editorial features free for the people we feature.${supportThanks}`);
            const refreshed = await getPortal(paymentSlug);
            if (!cancelled) setPortal(refreshed);
          }
        } catch (e) {
          if (!cancelled) setRequestNotice(e instanceof Error ? e.message : 'Payment was completed, but ROAM is still confirming the order.');
        }
      }
      const cleanUrl = `${window.location.pathname}${window.location.hash || ''}`;
      window.history.replaceState({}, '', cleanUrl);
    }
    finishReturn();
    return () => { cancelled = true; };
  }, [portal?.id, portal?.slug]);

  const featureMedia = useMemo(() => portal?.media.filter((item) => item.role !== 'select') ?? [], [portal]);
  const selects = useMemo(() => portal?.media.filter((item) => item.role === 'select') ?? [], [portal]);
  const cartItems = useMemo(() => selects.flatMap((item) => { const tier = selectCart[item.id]; return tier ? [{ item, tier, priceCents: tierPrice(item, tier) }] : []; }), [selects, selectCart]);
  const cartSubtotal = useMemo(() => cartItems.reduce((sum, entry) => sum + entry.priceCents, 0), [cartItems]);
  // Prioritize the newest uploaded feature film; older exports may remain in the library.
  // The participant's main player must use the latest video, never an old broken export.
  const videosNewestFirst = featureMedia
    .filter((item) => item.kind === 'video')
    .sort((a, b) => Date.parse(b.createdAt || '') - Date.parse(a.createdAt || ''));
  const primaryFeature = videosNewestFirst[0] || featureMedia.find((item) => item.kind === 'photo') || featureMedia[0];
  // Keep accompanying images and distinct supporting clips, but don't render obsolete
  // same-name exports as duplicate video players below the main film.
  const supportingFeatureMedia = featureMedia.filter((item) =>
    item.id !== primaryFeature?.id && !(
      item.kind === 'video' && primaryFeature?.kind === 'video' &&
      item.title.trim().toLowerCase() === primaryFeature.title.trim().toLowerCase()
    )
  );
  const shareUrl = useMemo(() => portal?.publicStoryUrl || '', [portal]);
  const showMediaSelects = selects.length > 0 || Boolean(portal?.mediaSelectOrders?.length);

  if (loading) return <div className="center-screen"><Brand /><p>Loading your ROAM feature…</p></div>;
  if (error || !portal) return <div className="center-screen"><Brand /><h1>We couldn’t open this feature.</h1><p>{error}</p></div>;

  const portalSlug = portal.slug;
  async function signOut() { await supabase?.auth.signOut(); navigate(`/login?next=${encodeURIComponent(`/portal/${portalSlug}`)}`, { replace: true }); }

  // Portal invites start at the Release itself: exactly two signing screens.
  // The standalone /release/:slug URL remains valid for pre-filming signatures.
  if (portal.requiresFeatureRelease && !portal.legal.featureRelease.acceptedAt) return <Navigate to={`/release/${encodeURIComponent(portal.slug)}?continue=portal`} replace />;

  if (!portal.legal.privateReview.acceptedAt) return (
    <main className="gate-page">
      <nav className="topbar">
        <Brand small />
        <div className="portal-account">
          <span className="private-label">Private</span>
          {!demoMode && <button className="text-link text-link--button" onClick={signOut}>Sign out</button>}
        </div>
      </nav>
      <AgreementGate
        document={portal.legal.privateReview}
        participantName={portal.participantName}
        initialTypedName={portal.legal.featureRelease.typedName || ''}
        onAccept={async (typedName) => {
          // The initial portal payload deliberately omits all protected media until
          // Private Page Terms are accepted. Re-fetch that payload after signing
          // rather than unlocking the page with its old empty media list.
          const result = await acceptPrivateReview(portal.slug, typedName);
          const acceptedPortal = {
            ...portal,
            legal: {
              ...portal.legal,
              privateReview: {
                ...portal.legal.privateReview,
                acceptedAt: result.acceptedAt,
                typedName: result.typedName,
              },
            },
          };
          if (demoMode) {
            setPortal(acceptedPortal);
            return;
          }
          try {
            const refreshedPortal = await getPortal(portal.slug);
            if (!refreshedPortal.legal.privateReview.acceptedAt) {
              throw new Error('Private access is not ready yet.');
            }
            setPortal(refreshedPortal);
          } catch {
            // The signature is already saved server-side; never tell the user
            // that signing failed just because the subsequent media read failed.
            setPortal(acceptedPortal);
            setRequestNotice('Your agreement is saved. We couldn’t load your media just yet. Please refresh this page to try again.');
          }
        }}
      />
    </main>
  );

  return <main className="portal-page">
    <nav className="topbar topbar--overlay"><Brand small /><div className="portal-account"><span className="private-label">Participant portal</span>{!demoMode && <button className="text-link text-link--button text-link--light" onClick={signOut}>Sign out</button>}</div></nav>
    <section className="feature-hero"><div className="feature-hero__scrim" /><div className="feature-hero__content"><div className="eyebrow">{portal.series}</div><h1>{portal.title}</h1><p>Prepared privately for {portal.participantName}</p></div></section>
    <section className="section section--intro"><div className="section__number">01</div><div><div className="eyebrow">THANK YOU FOR LETTING US IN</div><h2>Your story is the reason ROAM exists.</h2><p className="large-copy">{portal.thankYou}</p><p>Our editorial features remain free. Participation is never tied to purchasing media or commissioning commercial work.</p></div></section>
    <section className="section section--story">
      <div className="section__number">02</div>
      <div className="section__body story-layout">
        <div><div className="eyebrow">THE WRITTEN STORY</div><h2>{portal.storyTitle || 'Your ROAM story'}</h2></div>
        <article className="story-copy">{portal.storyBody
          ? portal.storyBody.split('\n').filter(Boolean).map((paragraph, i) => <p key={i}>{paragraph}</p>)
          : <p className="muted-copy">The written feature has not been added yet.</p>}
        </article>
      </div>
    </section>
    <section className="section section--feature">
      <div className="section__number">03</div>
      <div className="section__body">
        <div className="eyebrow">YOUR FREE ROAM FEATURE</div>
        <h2>{primaryFeature?.kind === 'photo' ? 'See the feature.' : 'Watch the feature.'}</h2>
        <div className={primaryFeature?.kind === 'photo' ? 'feature-stage feature-stage--photo' : 'feature-stage'}>
          {primaryFeature
            ? <ProtectedMedia item={primaryFeature} url={mediaUrls[primaryFeature.id]} eager />
            : <div className="media-placeholder"><strong>Feature media coming soon.</strong><span>The finished editorial feature will appear here.</span></div>}
        </div>
        <div className="feature-publication-approval">
          <PublicationReviewCard portal={portal} onReviewed={(approvedAt, typedName) => {
            setPortal((current) => current ? { ...current, publicationApproval: { ...current.publicationApproval, approvedAt, typedName } } : current);
            void getPortal(portal.slug).then(setPortal).catch(() => {
              setRequestNotice('Your review was recorded. You can refresh this page if needed.');
            });
          }} />
        </div>
        {supportingFeatureMedia.length > 0 && <div className="feature-support-grid">{supportingFeatureMedia.map((item) => <article key={item.id} className="support-media-card"><ProtectedMedia item={item} url={mediaUrls[item.id]} /><div><span>{item.kind}</span><strong>{item.title}</strong></div></article>)}</div>}
        {(portal.links.length > 0 || shareUrl) && <div className="feature-publishing-destinations">
          <div className="eyebrow">WHERE THE STORY TRAVELS</div>
          <h3>One story. Multiple places to discover it.</h3>
          {portal.links.length > 0 && <div className="platform-grid">{portal.links.map((link, i) => <a key={i} className="platform-card" href={link.url} target="_blank" rel="noreferrer"><span>{String(i + 1).padStart(2, '0')}</span><strong>{formatPlatform(link.platform)}</strong><small>{link.label || 'View feature'} ↗</small></a>)}</div>}
          {shareUrl && <div className="feature-publishing-destinations__share"><p>When you're ready to share, use your published story link—not this private review portal.</p><button className="button button--light" onClick={async () => { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }}>{copied ? 'Link copied' : 'Copy public feature link'}</button></div>}
        </div>}
      </div>
    </section>
    {showMediaSelects && <section className="section section--selects">
      <div className="section__number">04</div><div className="section__body">
        <div className="eyebrow">ROAM MEDIA SELECTS</div><h2>More from the story.</h2>
        <p className="section-lede">During every ROAM feature, we capture far more than what makes the final edit. These are a few selects from your shoot that we thought were worth keeping. The gallery uses protected previews; licensed delivery files are released through your order only when the applicable payment requirement is satisfied. Your editorial feature remains completely free; these optional requests are separate from the feature itself.</p>
        {requestNotice && <div className="notice">{requestNotice}</div>}
        <MediaSelectOrders portal={portal} onError={setRequestNotice} />
        {selects.length > 1 && <div className="select-carousel__controls"><span>{selects.length} selects</span><div><button type="button" aria-label="Previous Media Select" onClick={() => selectTrackRef.current?.scrollBy({ left: -(selectTrackRef.current.clientWidth * .78), behavior: 'smooth' })}>←</button><button type="button" aria-label="Next Media Select" onClick={() => selectTrackRef.current?.scrollBy({ left: selectTrackRef.current.clientWidth * .78, behavior: 'smooth' })}>→</button></div></div>}
        <div ref={selectTrackRef} className={selects.length === 1 ? 'select-carousel select-carousel--single' : 'select-carousel'}>{selects.map((item) => <MediaSelectCard key={item.id} item={item} url={mediaUrls[item.id]} selectedTier={selectCart[item.id]} onAdd={(mediaId, tier) => setSelectCart((current) => ({ ...current, [mediaId]: tier }))} onRemove={(mediaId) => setSelectCart((current) => { const next = { ...current }; delete next[mediaId]; return next; })} />)}</div>
        {cartItems.length > 0 && <div className="media-cart-bar"><div><span>{cartItems.length} selection{cartItems.length === 1 ? '' : 's'}</span><strong>{money(cartSubtotal)}</strong></div><button type="button" className="button button--dark" onClick={() => setCheckoutOpen(true)}>Review selections ({cartItems.length})</button></div>}
      </div>
    </section>}
    <section className="section section--commission"><div className="section__number">{showMediaSelects ? '05' : '04'}</div><div className="section__body two-col"><div><div className="eyebrow">COMMISSION ROAM · SEPARATE PRODUCTION</div><h2>Imagine what we could create if the production were built specifically around your business.</h2><p>ROAM Media Selects are moments already captured while making your editorial feature. Commission ROAM is different: a new project planned, shot and edited specifically for your brand, campaign or business goals.</p><div className="commission-tags"><span>Brand films</span><span>Drone production</span><span>Photography</span><span>Short-form content</span><span>Digital campaigns</span></div><small>Commercial work is separately scoped and priced. It has no effect on editorial selection, coverage or publication.</small></div><CommissionForm slug={portal.slug} /></div></section>
    <section className="section section--refer"><div className="section__number">{showMediaSelects ? '06' : '05'}</div><div className="section__body two-col"><div><div className="eyebrow">REFER ROAM · WHO SHOULD WE MEET NEXT?</div><h2>Every good story leads to another.</h2><p>If there is someone you think we should follow, meet or learn from, introduce us. Features remain free; curiosity is the only requirement.</p></div><ReferralForm onSubmit={(payload) => submitReferral(portal.slug, payload).then(() => undefined)} /></div></section>
    <section className="section section--signed-agreements">
      <div className="section__number">RECORDS</div>
      <div className="section__body">
        <div className="eyebrow">YOUR AGREEMENTS</div>
        <h2>Your copies, whenever you need them.</h2>
        <p>Your agreements are saved when you accept them. You don't need to sign the same version again on each visit. Copies include your typed signature, signing date and time, and the exact version you accepted.</p>
        <div className="signed-agreement-links">
          {portal.legal.featureRelease.acceptedAt && <button type="button" className="button button--light" onClick={async () => { try { await downloadSignedAgreement(portal.slug, 'feature_release'); } catch (e) { setRequestNotice(e instanceof Error ? e.message : 'Unable to get signed release.'); } }}>Download your Participant Release</button>}
          {portal.legal.privateReview.acceptedAt && <button type="button" className="button button--light" onClick={async () => { try { await downloadSignedAgreement(portal.slug, 'private_review'); } catch (e) { setRequestNotice(e instanceof Error ? e.message : 'Unable to get signed terms.'); } }}>Download your Private Page Terms</button>}
        </div>
        {requestNotice && <p role="status" className="microcopy">{requestNotice}</p>}
      </div>
    </section>
    {checkoutOpen && <MediaSelectCheckout portal={portal} items={cartItems} onClose={() => setCheckoutOpen(false)} onRemove={(mediaId) => setSelectCart((current) => { const next = { ...current }; delete next[mediaId]; if (Object.keys(next).length === 0) setCheckoutOpen(false); return next; })} onSubmitted={async (_message) => { setRequestNotice('Your zero-balance ROAM Media Select request is recorded. No payment was required. Thank you again for being part of the story and allowing ROAM to document it.'); setSelectCart({}); setCheckoutOpen(false); try { setPortal(await getPortal(portal.slug)); } catch { /* keep confirmation visible even if refresh is delayed */ } }} />}
    <footer className="portal-footer"><Brand /><p>People. Places. Experiences.</p><span>Private participant portal · ROAM GROUP</span></footer>
  </main>;
}
